import { Link } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { REPORT_SANDBOX_ATTRIBUTE } from '../../../../lib/hostedReportSandbox';
import { LIBRARY_DOCUMENT_COPY } from '../../../../lib/portalLibrary/listShapes';
import { librarySectionCrumb } from '../../../../lib/portalLibrary/breadcrumb';
import type { PortalLibraryOpenResult } from '../../../../types/portalLibrary';
import type { PortalViewerNav } from '../../../../lib/portalNav';

// CLIENT PORTAL LIBRARY — what one opened document LOOKS like, given the
// entry endpoint's answer. Pure: no fetching and no Firebase, so a test can
// render it from the endpoint's REAL response and assert on the HTML. (Since
// Round 5 the breadcrumb is a router <Link>, so it needs a Router ancestor — a
// test wraps it in a MemoryRouter.)
//
// ⚠️⚠️ THE TOKEN GUARD (Round 4 §1). Nothing here may carry the report's
// shareable-link token — not the iframe `src`, not a data attribute, not an
// embedded payload. The shareable link is permanent, so a token a browsing
// client could read would outlive their portal access. The iframe points at the
// ENTRY-keyed portal door; the test renders this with the endpoint's actual
// body and fails if the token appears anywhere in the markup.
//
// ⚠️⚠️ THE DOCUMENT IS NEVER RENDERED HERE — it is iframed from the content
// route, which serves it under the sandbox CSP in an opaque origin. The
// attribute below is derived from the same constant as that header, and a
// source guard holds this file to it (sandboxAttributeAgreement.test.ts).

export type LibraryDocumentState =
  | { kind: 'loading' }
  | { kind: 'not_available' }
  | { kind: 'error' }
  | { kind: 'open'; result: Extract<PortalLibraryOpenResult, { status: 'ok' }> };

export function LibraryDocumentView({
  state,
  nav,
  onRetry,
  spinner,
}: {
  state: LibraryDocumentState;
  /**
   * THIS viewer's nav (Round 6), or null while it loads — then the title shows
   * alone rather than a crumb built on a guessed nav shape.
   */
  nav: PortalViewerNav | null;
  onRetry?: () => void;
  spinner?: React.ReactNode;
}) {
  if (state.kind === 'loading') return <>{spinner ?? null}</>;
  if (state.kind !== 'open') {
    const notAvailable = state.kind === 'not_available';
    return (
      <div className="max-w-md">
        <h1 className="text-lg font-semibold text-foreground">
          {notAvailable ? LIBRARY_DOCUMENT_COPY.notAvailableTitle : LIBRARY_DOCUMENT_COPY.errorTitle}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {notAvailable ? LIBRARY_DOCUMENT_COPY.notAvailableBody : LIBRARY_DOCUMENT_COPY.errorBody}
        </p>
        {!notAvailable && onRetry && (
          <Button variant="outline" className="mt-3" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    );
  }
  const { title, section, contentPath } = state.result;
  // ⚠️ The crumb comes from THE VIEWER'S NAV (see breadcrumb.ts) — never a label
  // typed here, and never the other nav shape.
  const crumb = nav ? librarySectionCrumb(section, nav) : null;
  return (
    <>
      <nav aria-label="Breadcrumb" className="plib-crumbs">
        <ol>
          {crumb && (
            <>
              <li>
                <Link to={crumb.path}>{crumb.label}</Link>
              </li>
              <li aria-hidden="true">›</li>
            </>
          )}
          <li>
            <span aria-current="page">{title}</span>
          </li>
        </ol>
      </nav>
      <h1 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
      {/* ⚠️ Round 5: the frame FILLS the column's remaining height (fill mode on
          the shell) with a minimum — see .plib-doc-frame in index.css. */}
      <div className="plib-doc-frame">
        <iframe
          // ⚠️⚠️ NOT THE ENFORCEMENT — the content route's CSP header is. The
          // effective restriction is the union of the two, so this is derived
          // from the same constant rather than written out.
          sandbox={REPORT_SANDBOX_ATTRIBUTE}
          src={contentPath}
          title={title}
          className="h-full w-full border-0"
        />
      </div>
    </>
  );
}
